import React, { useMemo, useState } from 'react';
import { Button } from 'antd';
import { useDidUpdate } from 'rooks';
import moment from 'moment/moment';

import AssignmentActionModal, {
  AssignmentActionEnum,
} from 'components/modals/AssignmentActionModal';
import { AssignmentStatusTypeEnum } from 'api/adminApiClient';
import RenewalTypeModal from 'features/assignment/RenewAssignment/RenewalTypeModal';
import EditAssignment from '../../EditAssignment';
import TerminateAssignmentModal from 'components/modals/TerminateAssignmentModal';
import {
  AssignmentDetailsModel,
  isTerminationDateInFuture,
} from 'utils/assignmentUtils';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import {
  AssignmentTerminationModel,
  AvailableRenewOptionsEnum,
  BreakInServiceEnum,
  RenewAssignmentEnum,
  RenewKindEnum,
  RenewStatusEnum,
} from 'api/backofficeApiClient';
import RehireTypeSelectionModal from 'features/assignment/RehireAssignment/RehireActionSelectionModal';

type Props = {
  assignmentId: string;
  loadAssignmentDetails: () => Promise<void>;
  loadPrimaryAndTerminationData?: () => Promise<void>;
  isTemplate: boolean;
  assignmentDetails: AssignmentDetailsModel;
  isPartnerMode: boolean;
  assignmentTerminationDetails?: AssignmentTerminationModel;
  isCanTerminateAssignment?: boolean;
};

export default function AssignmentActionSection(props: Props) {
  const {
    assignmentId,
    loadAssignmentDetails,
    isTemplate,
    assignmentDetails,
    isPartnerMode,
    assignmentTerminationDetails,
    isCanTerminateAssignment,
    loadPrimaryAndTerminationData,
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isActionInfoModalOpen, setIsActionInfoModalOpen] = useState(false);
  const [isActionDetailsModalOpen, setIsActionDetailsModalOpen] =
    useState(false);
  const [activeAction, setActiveAction] = useState<
    AssignmentActionEnum | undefined
  >();
  const hasActiveScheduledTerminationInFuture = isTerminationDateInFuture(
    assignmentTerminationDetails?.terminationDate,
  );
  const isAdminRole = useAppSelector(isAdminSelector);
  const {
    renewStatus,
    assignmentStatus,
    availableRenewOptions,
    renewKind,
    renewType,
    customerInfo,
  } = assignmentDetails;
  const isDraftAssignment = assignmentStatus === AssignmentStatusTypeEnum.Draft;
  const isPendingAssignment =
    assignmentStatus === AssignmentStatusTypeEnum.Request;
  const isActiveAssignment =
    assignmentStatus === AssignmentStatusTypeEnum.Active;
  const isCompletedAssignment =
    assignmentStatus === AssignmentStatusTypeEnum.Completed;
  const isDirectEditModalOpen = isTemplate || isDraftAssignment;
  const isCanRenewAssignment =
    availableRenewOptions === AvailableRenewOptionsEnum.Renew;
  const isCanRehireAssignment =
    availableRenewOptions === AvailableRenewOptionsEnum.Rehire;
  const buttonText = isDirectEditModalOpen ? 'Edit' : 'More Actions';

  const isCanEditAssignment = useMemo(() => {
    if (isPartnerMode) return false;

    return (
      isDraftAssignment ||
      isActiveAssignment ||
      isPendingAssignment ||
      (isAdminRole && isCompletedAssignment)
    );
  }, [
    isDraftAssignment,
    isActiveAssignment,
    isCompletedAssignment,
    isPendingAssignment,
    isPartnerMode,
    isAdminRole,
  ]);

  const isDisabledAssignmentEdit =
    !isCanEditAssignment ||
    (!isAdminRole &&
      (renewStatus === RenewStatusEnum.RenewRequest ||
        renewStatus === RenewStatusEnum.ParentRenewRequest)) ||
    (renewStatus === RenewStatusEnum.Renewed &&
      renewType === RenewAssignmentEnum.WithChangeCustomer &&
      renewKind === RenewKindEnum.Rehire);

  const hasNoBreakInService =
    customerInfo?.breakInService === BreakInServiceEnum.Days0;

  const isDisabledAssignmentRenew =
    renewStatus === RenewStatusEnum.ParentRenewRequest ||
    isPendingAssignment ||
    hasNoBreakInService ||
    !isCanRenewAssignment;

  const isDisabledAssignmentRehire =
    renewStatus === RenewStatusEnum.ParentRenewRequest ||
    !isCanRehireAssignment;

  const isDisabledAssignmentTermination = !isCanTerminateAssignment;

  const hasOneOfAction =
    !isDisabledAssignmentEdit ||
    !isDisabledAssignmentRenew ||
    !isDisabledAssignmentTermination ||
    !isDisabledAssignmentRehire;

  const confirmationTitleForRenewing = hasActiveScheduledTerminationInFuture
    ? `The assignment is scheduled for termination on ${moment
        .utc(assignmentTerminationDetails?.terminationDate)
        .format(
          'MM/DD/YYYY',
        )}. By renewing this Assignment, you will cancel termination`
    : undefined;

  const confirmationTitleForRehiring =
    hasActiveScheduledTerminationInFuture && hasNoBreakInService
      ? `The assignment is scheduled for termination on ${moment
          .utc(assignmentTerminationDetails?.terminationDate)
          .format(
            'MM/DD/YYYY',
          )}. By rehiring this Assignment, you will cancel termination`
      : undefined;

  useDidUpdate(() => {
    !isLoading && handleActionInfoModalClose();
  }, [isLoading]);

  const handleLoadingChange = (loadingStatus: boolean) => {
    setIsLoading(loadingStatus);
  };

  const handleActionInfoModalOpen = () => {
    setActiveAction(AssignmentActionEnum.Edit);

    if (isDirectEditModalOpen) {
      setIsActionDetailsModalOpen(true);
    } else {
      setIsActionInfoModalOpen(true);
    }
  };

  const handleActionInfoModalClose = () => {
    setIsActionInfoModalOpen(false);
  };

  const handleActionDetailsModalOpen = () => {
    setIsActionDetailsModalOpen(true);
    activeAction !== AssignmentActionEnum.Edit && handleActionInfoModalClose();
  };

  const handleActionDetailsModalClose = () => {
    setIsActionDetailsModalOpen(false);
  };

  const handleActiveActionChange = (activeAction?: AssignmentActionEnum) => {
    setActiveAction(activeAction);
  };

  return (
    <>
      {isActionDetailsModalOpen &&
        activeAction === AssignmentActionEnum.Edit && (
          <EditAssignment
            isRenewalRequest={renewStatus === RenewStatusEnum.RenewRequest}
            assignmentId={assignmentId as string}
            onSubmit={loadAssignmentDetails}
            isDraft={isDraftAssignment}
            isTemplate={isTemplate}
            assignmentStatus={assignmentStatus}
            onClose={handleActionDetailsModalClose}
            isLoading={isLoading}
            handleLoadingChange={handleLoadingChange}
            renewKind={renewKind}
            renewType={renewType}
          />
        )}

      {isActionDetailsModalOpen &&
        activeAction === AssignmentActionEnum.Renew && (
          <RenewalTypeModal
            onSubmit={loadAssignmentDetails}
            assignmentId={assignmentId as string}
            onClose={handleActionDetailsModalClose}
            breakInServiceEnumValue={customerInfo?.breakInService}
            companyId={assignmentDetails?.companyId}
          />
        )}

      {isActionDetailsModalOpen &&
        activeAction === AssignmentActionEnum.Rehire && (
          <RehireTypeSelectionModal
            onSubmit={loadAssignmentDetails}
            assignmentId={assignmentId as string}
            onClose={handleActionDetailsModalClose}
            breakInServiceEnumValue={customerInfo?.breakInService}
            companyId={assignmentDetails?.companyId}
          />
        )}

      {isActionDetailsModalOpen &&
        activeAction === AssignmentActionEnum.Terminate && (
          <TerminateAssignmentModal
            onClose={handleActionDetailsModalClose}
            assignmentId={assignmentId}
            onSuccess={loadPrimaryAndTerminationData}
            assignmentTerminationDetails={assignmentTerminationDetails}
            isPendingAssignment={isPendingAssignment}
          />
        )}

      {isActionInfoModalOpen && (
        <AssignmentActionModal
          handleActiveActionChange={handleActiveActionChange}
          activeAction={activeAction}
          onClose={handleActionInfoModalClose}
          handleActionDetailsModalOpen={handleActionDetailsModalOpen}
          isLoading={isLoading}
          assignmentTerminationDetails={assignmentTerminationDetails}
          isDisabledAssignmentEdit={isDisabledAssignmentEdit}
          isDisabledAssignmentRenew={isDisabledAssignmentRenew}
          isDisabledAssignmentTermination={isDisabledAssignmentTermination}
          isDisabledAssignmentRehire={isDisabledAssignmentRehire}
          confirmationTitleForRenewing={confirmationTitleForRenewing}
          confirmationTitleForRehiring={confirmationTitleForRehiring}
        />
      )}

      <div>
        {hasOneOfAction && (
          <Button
            className="with-icon"
            onClick={handleActionInfoModalOpen}
            loading={isDirectEditModalOpen && isLoading}
          >
            <span className="icon icon-edit" />
            {buttonText}
          </Button>
        )}
      </div>
    </>
  );
}
